import React, {
  createContext,
  Fragment,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFloating, autoUpdate } from "@floating-ui/react";
import { twMerge } from "tailwind-merge";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import { Button, Icon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import styles from "./DropdownLegacy.module.css";

const DropdownContext = createContext<{
  setActive?: (active: boolean) => void;
}>({});

export const DropdownRoot = ({
  children,
  className = "",
  icon = "DOTS_VERTICAL",
}: {
  children: any;
  className?: string;
  icon?: IconType;
}): ReactElement | null => {
  const [active, setActive] = useState(false);
  const {
    refs: { setReference, setFloating },
    floatingStyles,
    update,
  } = useFloating({
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
  });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active && update) update();
  }, [active, update]);

  useOnClickOutside(ref, () => setActive(false));

  if (!children) return null;

  if (Array.isArray(children) && !children.filter(Boolean).length) return null;

  if (
    children?.type === Fragment &&
    !children.props.children.filter(Boolean).length
  )
    return null;

  return (
    <DropdownContext.Provider
      value={{
        setActive,
      }}
    >
      <div ref={ref} className={className} style={{ width: "fit-content" }}>
        <Button
          size={"small"}
          color={"transparent"}
          ref={setReference}
          onClick={() => setActive(!active)}
        >
          <Icon icon={icon} />
        </Button>
        {/*Leaving in for now as a reminder that we need to look for new transition module*/}
        {/*This still uses the deprecated findDOMNode that's not in React19 */}

        {/*<CSSTransition*/}
        {/*  in={active}*/}
        {/*  timeout={200}*/}
        {/*  mountOnEnter*/}
        {/*  unmountOnExit*/}
        {/*  classNames={{ ...styles }}*/}
        {/*>*/}
        {active && (
          <ul
            ref={setFloating}
            className={`rounded-sm shadow-md`}
            style={{
              ...floatingStyles,
              zIndex: 1,
            }}
          >
            {children}
          </ul>
        )}
        {/*</CSSTransition>*/}
      </div>
    </DropdownContext.Provider>
  );
};

export const Element = ({
  onClick,
  children,
  className,
}: {
  onClick?: () => void;
  children: string;
  className?: string;
}): ReactElement => {
  const { setActive } = useContext(DropdownContext);

  return (
    <li
      className={twMerge(`bg-white p-3 ${styles.element}`, className)}
      onClick={() => {
        if (onClick) onClick();
        setActive?.(false);
      }}
    >
      {children}
    </li>
  );
};

export { DropdownRoot as DropdownLegacy, Element as DropdownLegacyElement };
